import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

const INITIAL_STATE = {
  // Cache para datos de la API
  cache: {
    genders: { data: null, lastFetched: null, isLoading: false, error: null },
    occupations: { data: null, lastFetched: null, isLoading: false, error: null },
    howYouMeet: { data: null, lastFetched: null, isLoading: false, error: null },
    specialties: { data: null, lastFetched: null, isLoading: false, error: null },
    languages: { data: null, lastFetched: null, isLoading: false, error: null },
    locations: {
      countries: { data: null, lastFetched: null, isLoading: false, error: null },
      states: { data: {}, lastFetched: null, isLoading: false, error: null }, // Por país
      cities: { data: {}, lastFetched: null, isLoading: false, error: null }, // Por estado
    },
  },

  // Estados globales de la API
  apiStatus: {
    isOnline: true,
    lastConnected: null,
    retryCount: 0,
  },

  // Request tracking
  pendingRequests: [], // Para cancelar requests cuando sea necesario

  // Error handling
  globalApiError: null,
  lastApiError: null,
};

// Helper function para determinar si el cache es válido
const isCacheValid = (cacheItem, maxAge = 5 * 60 * 1000) => {
  // 5 minutos por defecto
  if (!cacheItem.data || !cacheItem.lastFetched) return false;
  return Date.now() - new Date(cacheItem.lastFetched).getTime() < maxAge;
};

// Async thunks para operaciones de API
export const fetchGenders = createAsyncThunk(
  'api/fetchGenders',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      // Verificar cache
      if (isCacheValid(state.api.cache.genders)) {
        return state.api.cache.genders.data;
      }

      // Aquí harías la llamada real a tu API
      // const response = await getGenders();

      // Mock data por ahora
      const mockData = [
        { id: 1, name: 'Masculino' },
        { id: 2, name: 'Femenino' },
        { id: 3, name: 'Otro' },
        { id: 4, name: 'Prefiero no decir' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchOccupations = createAsyncThunk(
  'api/fetchOccupations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.api.cache.occupations)) {
        return state.api.cache.occupations.data;
      }

      // Mock data
      const mockData = [
        { id: 1, name: 'Abogado' },
        { id: 2, name: 'Médico' },
        { id: 3, name: 'Ingeniero' },
        { id: 4, name: 'Empresario' },
        { id: 5, name: 'Estudiante' },
        { id: 6, name: 'Otro' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchHowYouMeet = createAsyncThunk(
  'api/fetchHowYouMeet',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.api.cache.howYouMeet)) {
        return state.api.cache.howYouMeet.data;
      }

      // Mock data
      const mockData = [
        { id: 1, name: 'Redes sociales' },
        { id: 2, name: 'Recomendación' },
        { id: 3, name: 'Búsqueda en Google' },
        { id: 4, name: 'Publicidad' },
        { id: 5, name: 'Otro' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSpecialties = createAsyncThunk(
  'api/fetchSpecialties',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.api.cache.specialties)) {
        return state.api.cache.specialties.data;
      }

      // Mock data
      const mockData = [
        { id: 1, name: 'Derecho Civil' },
        { id: 2, name: 'Derecho Penal' },
        { id: 3, name: 'Derecho Laboral' },
        { id: 4, name: 'Derecho Mercantil' },
        { id: 5, name: 'Derecho Familiar' },
        { id: 6, name: 'Derecho Fiscal' },
        { id: 7, name: 'Derecho Administrativo' },
        { id: 8, name: 'Derecho Constitucional' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLanguages = createAsyncThunk(
  'api/fetchLanguages',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.api.cache.languages)) {
        return state.api.cache.languages.data;
      }

      // Mock data
      const mockData = [
        { id: 1, name: 'Español', code: 'es' },
        { id: 2, name: 'Inglés', code: 'en' },
        { id: 3, name: 'Francés', code: 'fr' },
        { id: 4, name: 'Alemán', code: 'de' },
        { id: 5, name: 'Italiano', code: 'it' },
        { id: 6, name: 'Portugués', code: 'pt' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCountries = createAsyncThunk(
  'api/fetchCountries',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (isCacheValid(state.api.cache.locations.countries)) {
        return state.api.cache.locations.countries.data;
      }

      // Mock data
      const mockData = [
        { id: 1, name: 'México', code: 'MX' },
        { id: 2, name: 'Estados Unidos', code: 'US' },
        { id: 3, name: 'España', code: 'ES' },
        { id: 4, name: 'Colombia', code: 'CO' },
        { id: 5, name: 'Argentina', code: 'AR' },
      ];

      return mockData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchStates = createAsyncThunk(
  'api/fetchStates',
  async (countryId, { getState, rejectWithValue }) => {
    try {
      const state = getState();

      if (
        state.api.cache.locations.states.data[countryId] &&
        isCacheValid(state.api.cache.locations.states)
      ) {
        return { countryId, data: state.api.cache.locations.states.data[countryId] };
      }

      // Mock data para México
      let mockData = [];
      if (countryId === 1) {
        mockData = [
          { id: 1, name: 'Ciudad de México' },
          { id: 2, name: 'Jalisco' },
          { id: 3, name: 'Nuevo León' },
          { id: 4, name: 'Puebla' },
          { id: 5, name: 'Guanajuato' },
        ];
      }

      return { countryId, data: mockData };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk para inicializar datos esenciales
export const initializeApiData = createAsyncThunk(
  'api/initializeApiData',
  async (_, { dispatch }) => {
    try {
      await Promise.all([
        dispatch(fetchGenders()),
        dispatch(fetchOccupations()),
        dispatch(fetchHowYouMeet()),
        dispatch(fetchSpecialties()),
        dispatch(fetchLanguages()),
        dispatch(fetchCountries()),
      ]);
      return true;
    } catch (error) {
      throw error;
    }
  }
);

const apiSlice = createSlice({
  name: 'api',
  initialState: INITIAL_STATE,
  reducers: {
    // Cache Management
    clearCache: state => {
      Object.keys(state.cache).forEach(key => {
        if (typeof state.cache[key] === 'object' && state.cache[key].data !== undefined) {
          state.cache[key] = { data: null, lastFetched: null, isLoading: false, error: null };
        }
      });
    },

    clearCacheItem: (state, action) => {
      const item = action.payload;
      if (state.cache[item]) {
        state.cache[item] = { data: null, lastFetched: null, isLoading: false, error: null };
      }
    },

    // API Status
    setApiOnlineStatus: (state, action) => {
      state.apiStatus.isOnline = action.payload;
      if (action.payload) {
        state.apiStatus.lastConnected = new Date().toISOString();
        state.apiStatus.retryCount = 0;
      }
    },

    incrementRetryCount: state => {
      state.apiStatus.retryCount += 1;
    },

    resetRetryCount: state => {
      state.apiStatus.retryCount = 0;
    },

    // Error Handling
    setGlobalApiError: (state, action) => {
      state.globalApiError = action.payload;
      state.lastApiError = {
        error: action.payload,
        timestamp: new Date().toISOString(),
      };
    },

    clearGlobalApiError: state => {
      state.globalApiError = null;
    },

    // Request Tracking
    addPendingRequest: (state, action) => {
      state.pendingRequests.push({
        id: action.payload.id,
        type: action.payload.type,
        timestamp: new Date().toISOString(),
      });
    },

    removePendingRequest: (state, action) => {
      state.pendingRequests = state.pendingRequests.filter(req => req.id !== action.payload);
    },

    clearPendingRequests: state => {
      state.pendingRequests = [];
    },
  },

  extraReducers: builder => {
    // Generic handler for cache operations
    const handleCacheRequest = cacheKey => ({
      pending: state => {
        if (cacheKey.includes('.')) {
          // Handle nested cache (like locations.countries)
          const keys = cacheKey.split('.');
          let current = state.cache;
          for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]].isLoading = true;
          current[keys[keys.length - 1]].error = null;
        } else {
          state.cache[cacheKey].isLoading = true;
          state.cache[cacheKey].error = null;
        }
      },
      fulfilled: (state, action, cacheKey) => {
        if (cacheKey.includes('.')) {
          const keys = cacheKey.split('.');
          let current = state.cache;
          for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]].isLoading = false;
          current[keys[keys.length - 1]].data = action.payload;
          current[keys[keys.length - 1]].lastFetched = new Date().toISOString();
        } else {
          state.cache[cacheKey].isLoading = false;
          state.cache[cacheKey].data = action.payload;
          state.cache[cacheKey].lastFetched = new Date().toISOString();
        }
      },
      rejected: (state, action, cacheKey) => {
        if (cacheKey.includes('.')) {
          const keys = cacheKey.split('.');
          let current = state.cache;
          for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
          }
          current[keys[keys.length - 1]].isLoading = false;
          current[keys[keys.length - 1]].error = action.payload;
        } else {
          state.cache[cacheKey].isLoading = false;
          state.cache[cacheKey].error = action.payload;
        }
      },
    });

    // Apply handlers for each async thunk
    const genderHandlers = handleCacheRequest('genders');
    builder
      .addCase(fetchGenders.pending, genderHandlers.pending)
      .addCase(fetchGenders.fulfilled, (state, action) =>
        genderHandlers.fulfilled(state, action, 'genders')
      )
      .addCase(fetchGenders.rejected, (state, action) =>
        genderHandlers.rejected(state, action, 'genders')
      );

    const occupationHandlers = handleCacheRequest('occupations');
    builder
      .addCase(fetchOccupations.pending, occupationHandlers.pending)
      .addCase(fetchOccupations.fulfilled, (state, action) =>
        occupationHandlers.fulfilled(state, action, 'occupations')
      )
      .addCase(fetchOccupations.rejected, (state, action) =>
        occupationHandlers.rejected(state, action, 'occupations')
      );

    const howYouMeetHandlers = handleCacheRequest('howYouMeet');
    builder
      .addCase(fetchHowYouMeet.pending, howYouMeetHandlers.pending)
      .addCase(fetchHowYouMeet.fulfilled, (state, action) =>
        howYouMeetHandlers.fulfilled(state, action, 'howYouMeet')
      )
      .addCase(fetchHowYouMeet.rejected, (state, action) =>
        howYouMeetHandlers.rejected(state, action, 'howYouMeet')
      );

    const specialtyHandlers = handleCacheRequest('specialties');
    builder
      .addCase(fetchSpecialties.pending, specialtyHandlers.pending)
      .addCase(fetchSpecialties.fulfilled, (state, action) =>
        specialtyHandlers.fulfilled(state, action, 'specialties')
      )
      .addCase(fetchSpecialties.rejected, (state, action) =>
        specialtyHandlers.rejected(state, action, 'specialties')
      );

    const languageHandlers = handleCacheRequest('languages');
    builder
      .addCase(fetchLanguages.pending, languageHandlers.pending)
      .addCase(fetchLanguages.fulfilled, (state, action) =>
        languageHandlers.fulfilled(state, action, 'languages')
      )
      .addCase(fetchLanguages.rejected, (state, action) =>
        languageHandlers.rejected(state, action, 'languages')
      );

    const countryHandlers = handleCacheRequest('locations.countries');
    builder
      .addCase(fetchCountries.pending, countryHandlers.pending)
      .addCase(fetchCountries.fulfilled, (state, action) =>
        countryHandlers.fulfilled(state, action, 'locations.countries')
      )
      .addCase(fetchCountries.rejected, (state, action) =>
        countryHandlers.rejected(state, action, 'locations.countries')
      );

    // States handler (special case because it's by country)
    builder
      .addCase(fetchStates.pending, state => {
        state.cache.locations.states.isLoading = true;
        state.cache.locations.states.error = null;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.cache.locations.states.isLoading = false;
        state.cache.locations.states.data[action.payload.countryId] = action.payload.data;
        state.cache.locations.states.lastFetched = new Date().toISOString();
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.cache.locations.states.isLoading = false;
        state.cache.locations.states.error = action.payload;
      });

    // Initialize API Data
    builder.addCase(initializeApiData.fulfilled, state => {
      state.apiStatus.lastConnected = new Date().toISOString();
    });
  },
});

// Export actions
export const {
  clearCache,
  clearCacheItem,
  setApiOnlineStatus,
  incrementRetryCount,
  resetRetryCount,
  setGlobalApiError,
  clearGlobalApiError,
  addPendingRequest,
  removePendingRequest,
  clearPendingRequests,
} = apiSlice.actions;

// Selectors
export const selectApiCache = state => state.api.cache;
export const selectApiStatus = state => state.api.apiStatus;
export const selectGlobalApiError = state => state.api.globalApiError;
export const selectPendingRequests = state => state.api.pendingRequests;

// Specific cache selectors
export const selectGenders = state => state.api.cache.genders;
export const selectOccupations = state => state.api.cache.occupations;
export const selectHowYouMeet = state => state.api.cache.howYouMeet;
export const selectSpecialties = state => state.api.cache.specialties;
export const selectLanguages = state => state.api.cache.languages;
export const selectCountries = state => state.api.cache.locations.countries;
export const selectStates = countryId => state =>
  state.api.cache.locations.states.data[countryId] || [];

// Computed selectors
export const selectIsApiDataLoaded = state => {
  const cache = state.api.cache;
  return (
    cache.genders.data &&
    cache.occupations.data &&
    cache.howYouMeet.data &&
    cache.specialties.data &&
    cache.languages.data &&
    cache.locations.countries.data
  );
};

export const selectIsApiLoading = state => {
  const cache = state.api.cache;
  return (
    cache.genders.isLoading ||
    cache.occupations.isLoading ||
    cache.howYouMeet.isLoading ||
    cache.specialties.isLoading ||
    cache.languages.isLoading ||
    cache.locations.countries.isLoading ||
    cache.locations.states.isLoading
  );
};

export default apiSlice.reducer;
